// Base property sets for the Data tab, per asset type.
//
// The Data tab (see AssetDataTab) is the asset's property registry: for each
// asset type we define the ordered list of "base" properties — the fields that
// matter for that kind of asset — grouped by category. Base properties are
// shown even when empty, so users see and can fill the whole expected set.
//
// Compliance is intentionally excluded from every set: it is owned by the
// dedicated Compliance tab.
//
// This is curated frontend config for now. A later pass can move these lists
// into res-provisioning `02-asset-types.json` `property_codes` and read them
// from the API (GET …/asset-types/{uid}/properties).

/** One category block within an asset type's base set. */
export interface DataSection {
    /** property_category_code — drives the section heading + ordering. */
    category: string
    /** Ordered property definition codes shown in this section. */
    codes: string[]
}

/**
 * Display order of categories in the Data tab. The socle is now resolved from
 * the API (country-aware) via GET …/assets/{uid}/base-properties; this order is
 * used as a stable fallback and to place the Conformité (compliance) section —
 * which the per-country socle surfaces — after the descriptive categories.
 */
export const DATA_CATEGORY_ORDER = [
    'identity',
    'location',
    'surfaces',
    'occupation',
    'financial',
    'condition',
    'energy',
    'technical',
    'compliance',
    // The kpi_* values the group steering job publishes (spec 2026-10-04-01).
    'steering',
] as const

/** Categories the Data tab is allowed to show. */
export const DATA_CATEGORIES: string[] = [...DATA_CATEGORY_ORDER]

const SITE: DataSection[] = [
    { category: 'identity', codes: ['common_name', 'asset_class', 'building_type', 'client_internal_code', 'elevator_count'] },
    { category: 'location', codes: ['address_raw', 'postal_code', 'commune_code', 'cadastral_reference', 'rnb', 'flood_zone', 'seismic_zone', 'heritage_zone'] },
    { category: 'surfaces', codes: ['gross_floor_area', 'number_of_units', 'number_of_dwellings', 'number_of_floors'] },
    { category: 'occupation', codes: ['legal_ownership_form_type'] },
    { category: 'financial', codes: ['insured_value', 'charges_annuelles'] },
]

const BUILDING: DataSection[] = [
    { category: 'identity', codes: ['common_name', 'building_type', 'construction_date', 'client_internal_code', 'total_floor_count', 'has_basement', 'has_attic', 'has_elevator', 'elevator_count'] },
    { category: 'location', codes: ['address_raw', 'postal_code', 'rnb', 'building_id_national', 'cadastral_reference'] },
    { category: 'surfaces', codes: ['gross_floor_area', 'gross_internal_area', 'number_of_units', 'number_of_dwellings', 'number_of_floors', 'height_meters'] },
    { category: 'occupation', codes: ['legal_ownership_form_type', 'copropriete'] },
    { category: 'financial', codes: ['insured_value', 'charges_annuelles'] },
    { category: 'condition', codes: ['last_renovation_year', 'last_inspection_date', 'next_maintenance_date'] },
    { category: 'energy', codes: ['energy_rating', 'ghg_emissions_class', 'primary_energy_consumption', 'energy_source'] },
]

const UNIT: DataSection[] = [
    { category: 'identity', codes: ['unit_nb', 'unit_type', 'floor_label', 'floor_number'] },
    { category: 'surfaces', codes: ['surface_area', 'loi_carrez_m2', 'living_area', 'room_count', 'ceiling_height_m'] },
    { category: 'occupation', codes: ['unit_is_occupied', 'is_furnished', 'is_private'] },
    { category: 'financial', codes: ['charges_annuelles'] },
    { category: 'energy', codes: ['energy_rating', 'ghg_emissions_class'] },
]

/** Base sets keyed by asset type code. Used only as a loading fallback now
 * that the socle is resolved from the API. `parcel_land` reuses the site set;
 * `space_parking` and `outdoor` have no curated fallback (their socle comes
 * from the API). */
export const DATA_BASE_SETS: Record<string, DataSection[]> = {
    building_group: SITE,
    parcel_land: SITE,
    building: BUILDING,
    unit: UNIT,
}

/** Asset type codes that get the coherent Data tab. */
export const DATA_TAB_ASSET_TYPES = ['building_group', 'parcel_land', 'building', 'unit', 'space_parking', 'outdoor']

/** Flat, ordered list of base property codes for an asset type (or []). */
export function baseCodesFor(assetTypeCode?: string): string[] {
    if (!assetTypeCode) return []
    const sections = DATA_BASE_SETS[assetTypeCode]
    if (!sections) return []
    return sections.flatMap((s) => s.codes)
}
